<template>
  <PopupDialog
    v-model:open="open"
    :title="$t('global.version')"
    :description="description"
    content-class="bg-base-100 max-w-6xl"
  >
    <div class="space-y-6">
      <div v-if="updateAvailable" class="alert alert-warning alert-vertical sm:alert-horizontal">
        <FontAwesomeIcon :icon="faCloudArrowDown" class="text-2xl" />
        <div>
          <h3 class="font-bold">{{ $t('dialog.update-available.title') }}</h3>
          <p class="text-sm whitespace-pre-line">{{ $t('dialog.update-available.message') }}</p>
        </div>
        <div class="flex justify-end gap-2">
          <button type="button" class="btn btn-neutral" @click="restart">
            <FontAwesomeIcon :icon="faRotateRight" />
            {{ $t('dialog.update-available.restart') }}
          </button>
        </div>
      </div>
      <RadioGroupRoot
        v-model="activeTab"
        class="tabs tabs-box tabs-xl tabs-primary grid w-full grid-cols-1 sm:grid-cols-2"
        aria-label="Select package"
      >
        <RadioGroupItem
          v-for="pkg in packages"
          :key="pkg.value"
          :value="pkg.value"
          :aria-label="pkg.label"
          class="tab col-span-1 flex cursor-pointer gap-2"
          :class="{ 'tab-active': activeTab === pkg.value }"
        >
          <FontAwesomeIcon :icon="pkg.icon" />
          {{ pkg.label }}
        </RadioGroupItem>
      </RadioGroupRoot>
      <label class="label">
        <label class="toggle toggle-primary">
          <input type="checkbox" v-model="includePrerelease" />
          <FontAwesomeIcon :icon="faXmarkSquare" aria-label="disabled" class="mt-0.5 text-xs" />
          <FontAwesomeIcon :icon="faFlaskVial" aria-label="enabled" class="mt-0.5 text-xs" />
        </label>
        {{ $t('log.chaneglog.show-prereleases') }}
      </label>
      <div v-if="isFetching && !versions?.length" class="space-y-4">
        <div
          v-for="index in 5"
          :key="index"
          class="border-base-300 bg-base-200/50 rounded-box overflow-hidden border"
        >
          <div class="flex flex-wrap items-start justify-between gap-3 px-4 py-4">
            <div class="min-w-0 grow space-y-2">
              <div class="skeleton h-6 w-56 max-w-full" />
              <div class="skeleton h-4 w-40" />
            </div>
            <div class="flex items-center gap-2">
              <div class="skeleton h-6 w-20" />
              <div class="skeleton h-6 w-16" />
            </div>
          </div>
        </div>
      </div>
      <div v-else class="space-y-4">
        <div v-if="versions?.length" class="space-y-3">
          <CollapsibleRoot
            v-for="entry in versions"
            :key="entry.id"
            v-slot="{ open }"
            class="border-base-300 bg-base-200/50 rounded-box overflow-hidden border"
          >
            <CollapsibleTrigger
              class="hover:bg-base-300/30 flex w-full flex-wrap items-start justify-between gap-3 px-4 py-4 text-left transition-colors"
            >
              <div class="min-w-0 grow space-y-1">
                <div class="flex flex-wrap items-center gap-2">
                  <h3 class="truncate text-lg font-semibold">
                    {{ getVersionTitle(entry) }}
                  </h3>
                  <span v-if="entry.breaking" class="badge badge-warning badge-sm">
                    <FontAwesomeIcon :icon="faTriangleExclamation" />
                    Breaking
                  </span>
                </div>
                <p class="text-base-content/70 text-sm">
                  {{ formatPublishedAt(entry) }}
                </p>
              </div>
              <div class="flex flex-wrap items-center gap-2">
                <span class="badge badge-neutral font-mono">{{ entry.version }}</span>
                <span v-if="entry.channel" class="badge badge-primary capitalize">
                  {{ entry.channel }}
                </span>
              </div>
              <FontAwesomeIcon :icon="open ? faChevronUp : faChevronDown" class="self-center" />
            </CollapsibleTrigger>

            <CollapsibleContent class="border-base-300/50 border-t">
              <p v-if="getVersionSummary(entry)" class="text-base-content/80 p-4 text-sm leading-6">
                {{ getVersionSummary(entry) }}
              </p>

              <div v-if="entry.changes.length">
                <div
                  v-for="change in entry.changes"
                  :key="change.id"
                  class="odd:bg-base-300 px-4 py-2"
                  :class="{ 'bg-error odd:bg-error/80 text-error-content': change.breaking }"
                >
                  <div class="flex items-start gap-3">
                    <span class="badge badge-lg px-1" :class="getChangelogChangeTypeClass(change.type, 'badge')">
                      <FontAwesomeIcon :icon="getChangelogChangeTypeIcon(change.type)" />
                    </span>
                    <div class="relative min-w-0 grow space-y-2">
                      <div class="flex flex-wrap items-center gap-2">
                        <h4 class="font-semibold">
                          {{ getChangeSummary(change)?.title ?? fallbackChangeTitle }}
                        </h4>
                        <span
                          v-if="getChangeSummary(change)?.userConfirmationText"
                          class="alert alert-neutral"
                        >
                          <FontAwesomeIcon :icon="faExclamationTriangle" class="text-warning" />
                          <p>{{ getChangeSummary(change)?.userConfirmationText }}</p>
                        </span>
                      </div>
                      <p v-if="getChangeSummary(change)?.summary" class="text-sm leading-6">
                        {{ getChangeSummary(change)?.summary }}
                      </p>
                      <FontAwesomeIcon
                        v-if="change.breaking"
                        :icon="faExclamationTriangle"
                        class="text-warning absolute top-0 -right-2"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div v-else class="alert alert-info alert-soft sm:alert-horizontal">
                <span>No individual changes are available for this version.</span>
              </div>
            </CollapsibleContent>
          </CollapsibleRoot>
        </div>

        <div v-else class="alert alert-info alert-soft sm:alert-horizontal">
          <span>No versions found.</span>
        </div>

        <PaginationButtons
          v-if="meta && meta.totalItems > meta.itemsPerPage"
          :page-meta="meta"
          :page="currentPage"
          @update:page="currentPage = $event"
        />
      </div>
    </div>
  </PopupDialog>
</template>

<script setup lang="ts">
import {
  faChevronDown,
  faChevronUp,
  faCloudArrowDown,
  faDisplay,
  faExclamationTriangle,
  faFlaskVial,
  faRotateRight,
  faServer,
  faTriangleExclamation,
  faXmarkSquare,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import {
  getChangelogControllerGetVersionsQueryKey,
  type ResponseChangelogChangeDto,
  type ResponseChangelogChangeSummaryDto,
  type ResponseChangelogVersionDto,
  type ResponseChangelogVersionSummaryDto,
  useChangelogControllerGetVersions,
} from '@konsolenkost-de/acquisition_store_server_sdk_vue';
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  CollapsibleContent,
  CollapsibleRoot,
  CollapsibleTrigger,
  RadioGroupItem,
  RadioGroupRoot,
} from 'reka-ui';
import { useChangelogChangeTypeInfo } from '../../composables/changelog/useChangelogChangeTypeInfo';
import { usePwaUpdate } from '../../composables/usePwaUpdate';
import { useVersionHistoryDialog } from '../../composables/dialog/useVersionHistoryDialog';
import { useLoading } from '../../stores/loadingStore';
import PaginationButtons from '../form/PaginationButtons.vue';
import PopupDialog from './PopupDialog.vue';

const { open, hide } = useVersionHistoryDialog();
const { updateAvailable, applyUpdate } = usePwaUpdate();

// Closed first, so the splash screen the restart puts up is not left sitting
// behind an open changelog until the reload lands.
const restart = () => {
  hide();
  return applyUpdate();
};

const { watchLoading } = useLoading();

const activeTab = ref('client');
const currentPage = ref(1);
const includePrerelease = ref(false);
const pageSize = 5;

const packages = computed(() => [
  { value: 'client', icon: faDisplay, label: t('global.client') },
  { value: 'server', icon: faServer, label: t('global.server') },
]);

const { locale, t } = useI18n();

const normalizedLocale = computed(() => {
  return locale.value.toLowerCase().startsWith('de') ? 'de-DE' : 'en-US';
});

const description = computed(() => {
  return activeTab.value === 'client'
    ? 'Latest client releases and individual changes.'
    : 'Latest server releases and individual changes.';
});

watch(activeTab, () => {
  currentPage.value = 1;
});

const queryParams = computed(() => ({
  page: currentPage.value,
  limit: pageSize,
  'filter.package': [activeTab.value],
  'filter.prerelease': includePrerelease.value ? undefined : ['$null'],
}));

// The dialog is mounted for the whole app lifetime, so the changelog is only
// queried while it is actually open - otherwise it fires on every app start,
// login page included, where the endpoint can only answer 401.
const query = useChangelogControllerGetVersions(queryParams, {
  query: {
    queryKey: getChangelogControllerGetVersionsQueryKey(queryParams),
    enabled: open,
  },
});

const response = computed(() => query.data.value);

const versions = computed(() =>
  response.value?.status === 200 ? (response.value.data.data ?? []) : [],
);
const meta = computed(() =>
  response.value?.status === 200 ? response.value.data.meta : undefined,
);
const isFetching = query.isFetching;

// Kept locally as well: the skeleton below only replaces the list while the
// very first page is still empty, the global bar covers every later page.
watchLoading(isFetching);

const fallbackChangeTitle = computed(() => t('global.unknown'));

const getLocalizedSummary = <T extends { locale: string }>(summaries?: T[]) => {
  if (!summaries?.length) {
    return undefined;
  }

  const exactMatch = summaries.find((summary) => summary.locale === normalizedLocale.value);
  if (exactMatch) {
    return exactMatch;
  }

  const localePrefix = normalizedLocale.value.slice(0, 2);
  const languageMatch = summaries.find((summary) => summary.locale.startsWith(localePrefix));
  return languageMatch ?? summaries[0];
};

const formatPublishedAt = (entry: ResponseChangelogVersionDto) => {
  return new Intl.DateTimeFormat(normalizedLocale.value, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(entry.publishedAt));
};

const getVersionTitle = (entry: ResponseChangelogVersionDto) => {
  return (
    getLocalizedSummary<ResponseChangelogVersionSummaryDto>(entry.summaries)?.title ?? entry.version
  );
};

const getVersionSummary = (entry: ResponseChangelogVersionDto) => {
  return getLocalizedSummary<ResponseChangelogVersionSummaryDto>(entry.summaries)?.summary ?? '';
};

const getChangeSummary = (change: ResponseChangelogChangeDto) => {
  return getLocalizedSummary<ResponseChangelogChangeSummaryDto>(change.summaries);
};

const { getChangelogChangeTypeClass, getChangelogChangeTypeIcon, getChangelogChangeTypeTitle } =
  useChangelogChangeTypeInfo();
</script>
